import { i18next } from '@yola/ws-sdk';
import advancedColorSectionIds from '../constants/advanced-color-section-ids';
import variableNames from '../constants/variable-names';
import getAdvancedColorVariableDescriptors from './get-advanced-color-variable-descriptors';
import getColorFromVariables from './get-color-from-variables';

const SECTION_ORDER = [
  advancedColorSectionIds.GENERAL,
  advancedColorSectionIds.TYPOGRAPHY,
  advancedColorSectionIds.NAV_LINKS,
  advancedColorSectionIds.FOOTER_NAV_LINKS,
  advancedColorSectionIds.PRIMARY_BUTTON,
  advancedColorSectionIds.SECONDARY_BUTTON,
  advancedColorSectionIds.MEDIA,
  advancedColorSectionIds.DECORATIVE_ELEMENTS,
  advancedColorSectionIds.FORM,
];

const getSectionTitle = (sectionId) => {
  switch (sectionId) {
    case advancedColorSectionIds.GENERAL:
      return i18next.t('General');
    case advancedColorSectionIds.TYPOGRAPHY:
      return i18next.t('Typography');
    case advancedColorSectionIds.NAV_LINKS:
      return i18next.t('Header navigation');
    case advancedColorSectionIds.FOOTER_NAV_LINKS:
      return i18next.t('Footer navigation');
    case advancedColorSectionIds.PRIMARY_BUTTON:
      return i18next.t('Primary button');
    case advancedColorSectionIds.SECONDARY_BUTTON:
      return i18next.t('Secondary button');
    case advancedColorSectionIds.MEDIA:
      return i18next.t('Media');
    case advancedColorSectionIds.DECORATIVE_ELEMENTS:
      return i18next.t('Decorative Elements');
    case advancedColorSectionIds.FORM:
      return i18next.t('Form');
    default:
      return '';
  }
};

const getColorItemTitle = (variableName) => {
  switch (variableName) {
    case variableNames.BLOCK_BACKGROUND:
      return i18next.t('Block background');
    case variableNames.SITE_NAME:
      return i18next.t('Site name');
    case variableNames.HEADING_1:
      return i18next.t('Heading {number}', { number: 1 });
    case variableNames.HEADING_2:
      return i18next.t('Heading {number}', { number: 2 });
    case variableNames.HEADING_3:
      return i18next.t('Heading {number}', { number: 3 });
    case variableNames.HEADING_4:
      return i18next.t('Heading {number}', { number: 4 });
    case variableNames.HEADING_5:
      return i18next.t('Heading {number}', { number: 5 });
    case variableNames.HEADING_6:
      return i18next.t('Heading {number}', { number: 6 });
    case variableNames.BLOCKQUOTE:
      return i18next.t('Quote');
    case variableNames.PARAGRAPH:
      return i18next.t('Paragraph');
    case variableNames.LINK_TEXT:
      return i18next.t('Text link');
    case variableNames.MARK_TEXT:
      return i18next.t('Price label');
    case variableNames.HEADER_PHONE:
      return i18next.t('Phone number in header');
    case variableNames.NAV_LINK:
    case variableNames.FOOTER_NAV_LINK_TEXT:
      return i18next.t('Item default');
    case variableNames.NAV_LINK_ACTIVE:
      return i18next.t('Item active');
    case variableNames.OVERLAY_BACKGROUND:
      return i18next.t('Background overlay');
    case variableNames.BORDER:
      return i18next.t('Border');
    case variableNames.BLOCK_DIVIDER:
      return i18next.t('Block divider');
    case variableNames.FEATURE_ICON:
      return i18next.t('Feature icons');
    case variableNames.DECORATION_BACKGROUND:
      return i18next.t('Text effects');
    case variableNames.SOCIAL_ICON:
      return i18next.t('Social icons');
    case variableNames.CONTACT_ICON:
      return i18next.t('Contact information icons');
    case variableNames.INPUT_LABEL:
      return i18next.t('Input label');
    case variableNames.INPUT_BORDER:
      return i18next.t('Input field border');
    case variableNames.INPUT_TEXT:
      return i18next.t('Input field text');
    default:
      if (variableName.includes('_background')) {
        return i18next.t('Background color');
      }

      if (variableName.includes('_text')) {
        return i18next.t('Text color');
      }

      if (variableName.includes('_border')) {
        return i18next.t('Border color');
      }

      return '';
  }
};

const getGradient = ({ variableName, variables }) => {
  const gradient = variables[`${variableName}_gradient`] || null;

  return gradient && gradient !== 'none' ? gradient : null;
};

const createGenerateColorItem =
  ({ variables, overrides }) =>
  ({ variableName, withOpacity, withGradient }) => {
    const color = getColorFromVariables(`${variableName}_color`, variables);

    if (!color) return null;

    return {
      title: getColorItemTitle(variableName),
      variableName,
      color: `hsl(${color})`,
      isOverridden: variableName in overrides,
      ...(withOpacity && {
        opacity: variables[`${variableName}_color_opacity`],
      }),
      ...(withGradient && {
        gradient: getGradient({ variableName, variables }),
      }),
    };
  };

const getAdvancedColorsSections = ({
  variables,
  overrides,
  buttonPreset,
  isTextDecorationEnabled,
}) => {
  const generateColorItem = createGenerateColorItem({ variables, overrides });

  const descriptors = getAdvancedColorVariableDescriptors(buttonPreset).filter(
    (item) => !item.requiresTextDecoration || isTextDecorationEnabled
  );

  const descriptorsBySection = descriptors.reduce((acc, descriptor) => {
    if (!acc[descriptor.sectionId]) acc[descriptor.sectionId] = [];
    acc[descriptor.sectionId].push(descriptor);
    return acc;
  }, {});

  return SECTION_ORDER.map((sectionId) => {
    const colorItems = (descriptorsBySection[sectionId] || [])
      .map(generateColorItem)
      .filter(Boolean);

    if (!colorItems.length) return null;

    return {
      id: sectionId,
      title: getSectionTitle(sectionId),
      colorItems,
    };
  }).filter(Boolean);
};

export default getAdvancedColorsSections;
